// RegisterForm.tsx
"use client";
import "client-only";

import React, { useActionState, useEffect, useState } from "react";
import styles from './styles/form.module.css'
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormState } from "./formActions";
import { useToast } from "../toast/Toast";

interface Props {
  formAction: (state: FormState, formData: FormData) => Promise<FormState>;
  formType: "login" | "signup";
}

const meta = {
  login: {
    title: "Welcome Back",
    subTitle: "Please login to your account",
    buttonText: "Login",
    linkText: "Sign up",
    linkSubTitle: "Don't have an account? ",
    linkHref: "/register",
  },
  signup: {
    title: "Create Account",
    subTitle: "Sign up to get started",
    buttonText: "Create Account",
    linkText: "Login",
    linkSubTitle: "Already have an account? ",
    linkHref: "/login",
  },
};

const Form: React.FC<Props> = ({ formAction, formType }) => {
  const router = useRouter();
  const { showToast } = useToast();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formState, newFormAction, isPending] = useActionState(formAction, {});
  const metadata = meta[formType];

  useEffect(() => {
    if (formState.message)
      showToast(formState.message, "success", "top-right", 5000);
    if (formState.success) router.push("/");
  }, [formState]);

  const handleGoogleRegister = () => {
    console.log("Google register clicked");
    alert("Google OAuth registration would be implemented here!");
  };

  return (
    <div className={styles.body}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{metadata.title}</h1>
          <p className={styles.subtitle}>{metadata.subTitle}</p>
        </div>

        <form action={newFormAction}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={styles.input}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.inputWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className={`${styles.input} ${styles.inputPassword}`}
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          {formType === "login" ? (
            <div className={styles.formOptions}>
              <div className={styles.rememberMe}>
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  className={styles.checkbox}
                  required
                />
                <label htmlFor="remember" className={styles.checkboxLabel}>
                  Remember me
                </label>
              </div>
              <Link href="/forgot-password" className={styles.forgotPassword}>
                Forgot Password?
              </Link>
            </div>
          ) : (
            <div className={styles.termsWrapper}>
              <input
                type="checkbox"
                id="terms"
                name="terms"
                className={styles.checkbox}
                required
              />
              <label htmlFor="terms" className={styles.termsLabel}>
                I agree to the{" "}
                <a href="#" className={styles.termsLink}>
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a href="#" className={styles.termsLink}>
                  Privacy Policy
                </a>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={isPending}
            style={isPending ? { opacity: 0.4 } : { opacity: 1 }}
            className={styles.submitButton}
          >
            {isPending ? "Loading..." : metadata.buttonText}
          </button>
        </form>

        <div className={styles.divider}>
          <div className={styles.dividerLine} />
          <span className={styles.dividerText}>OR</span>
          <div className={styles.dividerLine} />
        </div>

        <button onClick={handleGoogleRegister} className={styles.googleButton}>
          <svg className={styles.googleIcon} viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <div className={styles.linkContainer}>
          {metadata.linkSubTitle}
          <Link href={metadata.linkHref} className={styles.linkAnchor}>
            {metadata.linkText}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Form;
